import { useState, useRef, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { X, Clock, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getSearchHistory,
  addSearchTerm,
  removeSearchTerm,
  clearSearchHistory,
} from "@/lib/searchHistoryCache";

interface SearchWithHistoryProps {
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit?: (value: string) => void;
  historyKey: string;
  disabled?: boolean;
  className?: string;
  icon?: React.ReactNode;
}

export function SearchWithHistory({
  placeholder,
  value,
  onChange,
  onSubmit,
  historyKey,
  disabled,
  className,
  icon,
}: SearchWithHistoryProps) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const refreshHistory = useCallback(() => {
    setHistory(getSearchHistory(historyKey));
  }, [historyKey]);

  const filtered = history.filter((term) =>
    !value.trim() || term.toLowerCase().includes(value.toLowerCase())
  );

  const handleFocus = () => {
    refreshHistory();
    setOpen(true);
  };

  const handleSelect = (term: string) => {
    onChange(term);
    onSubmit?.(term);
    setOpen(false);
  };

  const handleRemove = (e: React.MouseEvent, term: string) => {
    e.stopPropagation();
    removeSearchTerm(historyKey, term);
    refreshHistory();
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearSearchHistory(historyKey);
    setHistory([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && value.trim()) {
      addSearchTerm(historyKey, value);
      onSubmit?.(value);
      setOpen(false);
    }
    if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Close when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full">
      <Popover open={open && filtered.length > 0 && !disabled}>
        <PopoverTrigger asChild>
          <div>
            {icon}
            <Input
              ref={inputRef}
              placeholder={placeholder}
              value={value}
              onChange={(e) => {
                onChange(e.target.value);
                if (!open) {
                  refreshHistory();
                  setOpen(true);
                }
              }}
              onFocus={handleFocus}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              className={className}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[var(--radix-popover-trigger-width)]"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="max-h-[240px] overflow-y-auto">
            <div className="flex items-center justify-between px-3 py-2 border-b border-border">
              <span className="text-xs font-medium text-muted-foreground">Recent searches</span>
              <button
                onClick={handleClearAll}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                <Trash2 className="h-3 w-3" />
                Clear all
              </button>
            </div>
            {filtered.map((term) => (
              <button
                key={term}
                onClick={() => handleSelect(term)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-accent/50 transition-colors text-left group"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="truncate">{term}</span>
                </span>
                <button
                  onClick={(e) => handleRemove(e, term)}
                  className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity flex-shrink-0 ml-2"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
